import { useState } from "react";
import {
  Stack,
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Badge,
  Card,
  CardBody,
  CardFooter,
  Heading,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Icon,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiSearch, FiPlus, FiBook, FiEye, FiEdit, FiTrash2 } from "react-icons/fi";

const KnowledgeBase = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Mock data
  const articles = [
    {
      id: "1",
      title: "Getting Started with the Platform",
      description: "A comprehensive guide to help new users navigate and use the platform effectively.",
      category: "User Guide",
      author: "Admin",
      views: 1250,
      lastUpdated: "2024-02-15",
      isPublished: true,
    },
    {
      id: "2",
      title: "Driver Onboarding Process",
      description: "Step-by-step instructions for onboarding new drivers to the platform.",
      category: "Operations",
      author: "Operations Team",
      views: 890,
      lastUpdated: "2024-02-10",
      isPublished: true,
    },
    {
      id: "3",
      title: "Payment Gateway Integration",
      description: "Technical documentation for integrating payment gateways.",
      category: "Technical",
      author: "Tech Team",
      views: 456,
      lastUpdated: "2024-02-08",
      isPublished: true,
    },
    {
      id: "4",
      title: "Handling Customer Complaints",
      description: "Best practices for resolving customer complaints efficiently.",
      category: "Support",
      author: "Support Team",
      views: 678,
      lastUpdated: "2024-02-05",
      isPublished: false,
    },
  ];

  const categories = ["all", "User Guide", "Operations", "Technical", "Support", "Policies"];

  const categoryColors = {
    "User Guide": "blue",
    "Operations": "green",
    "Technical": "purple",
    "Support": "orange",
    "Policies": "red",
  };

  const filteredArticles = articles.filter(article => {
    const matchesCategory = categoryFilter === "all" || article.category === categoryFilter;
    const matchesSearch = !searchInput || 
      article.title.toLowerCase().includes(searchInput.toLowerCase()) ||
      article.description.toLowerCase().includes(searchInput.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/support", title: "Support Center" },
          { link: "#", title: "Knowledge Base", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Knowledge Base
          </Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
            Add Article
          </Button>
        </Flex>

        <Flex gap={4} mb={6}>
          <InputGroup maxW="400px">
            <InputLeftElement>
              <FiSearch color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search articles..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              borderRadius="lg"
            />
          </InputGroup>
          <Select
            maxW="200px"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === "all" ? "All Categories" : cat}
              </option>
            ))}
          </Select>
        </Flex>

        {filteredArticles.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No articles found</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {filteredArticles.map((article) => (
              <Card key={article.id} variant="outline" _hover={{ shadow: "md" }}>
                <CardBody>
                  <Flex justify="space-between" align="flex-start" mb={2}>
                    <Icon as={FiBook} boxSize={6} color="blue.500" />
                    <Flex gap={2}>
                      <Badge colorScheme={categoryColors[article.category] || "gray"}>
                        {article.category}
                      </Badge>
                      <Badge colorScheme={article.isPublished ? "green" : "gray"}>
                        {article.isPublished ? "Published" : "Draft"}
                      </Badge>
                    </Flex>
                  </Flex>
                  <Heading size="sm" mb={2}>{article.title}</Heading>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {article.description}
                  </Text>
                  <Flex mt={3} gap={4} fontSize="xs" color="gray.500">
                    <Flex align="center" gap={1}>
                      <FiEye />
                      <Text>{article.views} views</Text>
                    </Flex>
                    <Text>Updated: {article.lastUpdated}</Text>
                  </Flex>
                </CardBody>
                <CardFooter pt={0}>
                  <Flex gap={2}>
                    <Button size="sm" leftIcon={<FiEye />} variant="outline">
                      View
                    </Button>
                    <Button size="sm" leftIcon={<FiEdit />} variant="outline">
                      Edit
                    </Button>
                    <Button size="sm" leftIcon={<FiTrash2 />} variant="outline" colorScheme="red">
                      Delete
                    </Button>
                  </Flex>
                </CardFooter>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Add Article Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Article</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input placeholder="Enter article title" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea placeholder="Brief description of the article" rows={2} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Content</FormLabel>
                <Textarea placeholder="Article content (supports Markdown)" rows={8} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select placeholder="Select category">
                  <option value="User Guide">User Guide</option>
                  <option value="Operations">Operations</option>
                  <option value="Technical">Technical</option>
                  <option value="Support">Support</option>
                  <option value="Policies">Policies</option>
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue">Save as Draft</Button>
            <Button colorScheme="green" ml={2}>Publish</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default KnowledgeBase;
